import { TOKEN_ADDRESS } from "@silo/config";
import { makeUsd, ProdChainKey, ServerError } from "@silo/types";
import { Effect, Fiber, pipe } from "effect";
import { getCoinGeckoPriceUsd } from "../../coin-gecko/get-coin-gecko-price-usd";
import { KvClientError, KvService, KvTokenPrice } from "../../kv-client/types";
import { Schema } from "@effect/schema";
import { getTokenPriceKey } from "../../kv-client/keys";
import { TTL } from "../../kv-client/constants";

export type UpdateRewardTokenPrice = ReturnType<typeof updateRewardTokenPrice>;
export type UpdateRewardTokenPriceSuccess =
  Effect.Effect.Success<UpdateRewardTokenPrice>;
export type UpdateRewardTokenPriceError =
  Effect.Effect.Error<UpdateRewardTokenPrice>;
export type UpdateRewardTokenPriceContext =
  Effect.Effect.Context<UpdateRewardTokenPrice>;

export function updateRewardTokenPrice() {
  return Effect.gen(function* () {
    const setSiloPriceFiber = yield* pipe(setSiloPrice(), Effect.fork);

    yield* Fiber.join(setSiloPriceFiber);
  });
}

/**
 * If a new chain is added, check on this logic
 */
export const SILO_PRICE_GUARD: Record<ProdChainKey, true> = {
  arbitrum: true,
  optimism: true,
  base: true,
  ethereum: true,
};

function setSiloPrice() {
  return Effect.gen(function* () {
    const { value } = yield* Effect.tryPromise({
      try: () => getCoinGeckoPriceUsd("silo-finance"),
      catch: (cause) => new ServerError({ cause }),
    });

    const encodedPriceUsd = yield* pipe(
      makeUsd(value),
      Schema.encode(KvTokenPrice)
    );

    const kv = yield* KvService;

    yield* Effect.tryPromise({
      try: async () => {
        const pipeline = kv.pipeline();

        const arbitrumKey = getTokenPriceKey(
          "arbitrum",
          TOKEN_ADDRESS.arbitrum.SILO
        );

        const optimismKey = getTokenPriceKey(
          "optimism",
          TOKEN_ADDRESS.optimism.SILO
        );

        const baseKey = getTokenPriceKey("base", TOKEN_ADDRESS.base.SILO);

        const ethereumKey = getTokenPriceKey(
          "ethereum",
          TOKEN_ADDRESS.ethereum.SILO
        );

        pipeline.set(arbitrumKey, encodedPriceUsd, { ex: TTL.ONE_DAY });
        pipeline.set(optimismKey, encodedPriceUsd, { ex: TTL.ONE_DAY });
        pipeline.set(baseKey, encodedPriceUsd, { ex: TTL.ONE_DAY });
        pipeline.set(ethereumKey, encodedPriceUsd, { ex: TTL.ONE_DAY });

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    yield* Effect.log(`SILOv1 price updated`);
  });
}
